import { Button, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, Box } from "@mui/material";
import { useEffect, useState } from "react";
import CheckIcon from '@mui/icons-material/Check';
import MyRichTextEditor from "../../../components/MyRichTextEditor";
import { uploadEmailFile, generateUniqueImageKey, fetchEmailHtml } from "../../../api/emailConfigApi";
import { EMAIL_TYPES } from ".";

interface TemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (htmlContent: string, typeId: number) => Promise<void>;
  ruc: string;
  emailType: EMAIL_TYPES;
  typeId: number;
  initialHtml?: string;
}

interface PendingImage {
  file: File;
  localUrl: string;
}

const EMAIL_VARIABLES = [
  { code: "{{Nombre}}", description: "Nombre completo del colaborador" },
  { code: "{{Fecha}}", description: "Fecha del onomástico (DD/MM)" },
  { code: "{{Empresa}}", description: "Nombre de la organización" }
];

const TemplateEditorModal: React.FC<TemplateModalProps> = ({ 
  open, onClose, onSave, ruc, emailType, typeId 
}) => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingHtml, setLoadingHtml] = useState<boolean>(false);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);

  useEffect(() => {
  if (!open) return;
  
  const loadHtml = async () => {
    setLoadingHtml(true);
    try {
      const htmlFromS3 = await fetchEmailHtml(ruc, emailType);
      if (htmlFromS3) {
        setHtmlContent(htmlFromS3);
      } else {
        setHtmlContent("<p>¡Hola! Te deseamos un excelente cumpleaños...</p>");
      }
      setPendingImages([]);
    } catch (error) {
      console.error("Error al cargar HTML desde S3:", error);
      setHtmlContent("<p>¡Hola! Te deseamos un excelente cumpleaños...</p>");
    } finally {
      setLoadingHtml(false);
    }
  };

  loadHtml();
}, [open, ruc, emailType]);

  const handleRegisterLocalImage = (file: File, localUrl: string) => {
    setPendingImages(prev => [...prev, { file, localUrl }]);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let finalHtml = htmlContent;

      for (const item of pendingImages) {
        if (finalHtml.includes(item.localUrl)) {
          const uniqueKey = generateUniqueImageKey(item.file.name);
          const publicUrl = await uploadEmailFile(ruc, emailType, uniqueKey, item.file);
          if (publicUrl) {
            finalHtml = finalHtml.replaceAll(item.localUrl, publicUrl);
          }
        }
      }

      const htmlBlob = new Blob([finalHtml], { type: 'text/html' });
      const htmlFile = new File([htmlBlob], 'body.html', { type: 'text/html' });
      await uploadEmailFile(ruc, emailType, 'body.html', htmlFile);

      await onSave(finalHtml, typeId);
      onClose();
    } catch (error) {
      console.error("Error guardando la plantilla", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: '700' }}>
        Diseñador Visual de Plantilla de Correo
      </DialogTitle>
      <DialogContent dividers>
        {loadingHtml ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <MyRichTextEditor 
            value={htmlContent} 
            onChange={setHtmlContent} 
            variables={EMAIL_VARIABLES}
            onLocalImageAdded={handleRegisterLocalImage}
          />
        )}
      </DialogContent>
      <DialogActions style={{ padding: "1rem" }}>
        <Button onClick={onClose} color="primary" disabled={loading || loadingHtml}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={loading || loadingHtml}
          startIcon={<CheckIcon />}
        >
          Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditorModal;